/**
 * ODKAZY NA SOCIÁLNÍ SÍTĚ (#31).
 *
 * KDE TO JE ULOŽENÉ A PROČ
 * ------------------------
 * V konfiguraci sestavení (`VITE_SOCIAL_*`), ne v databázi a ne natvrdo
 * v komponentě. Zvažovaly se tři možnosti:
 *
 * 1. **Natvrdo v kódu** — vyloučeno zadáním, a právem: změna profilu by
 *    znamenala zásah do komponenty a code review.
 * 2. **Nastavení v administraci** — nejpohodlnější pro obsluhu, ale stojí
 *    migraci, admin routu, obrazovku v administraci a veřejný endpoint.
 *    To všechno kvůli čtyřem odkazům, které se mění jednou za život obchodu.
 *    Navíc by se buď načítaly zvlášť (další požadavek na KAŽDÉ vykreslení
 *    stránky, napořád), nebo by se musely přibalit do vzhledu.
 * 3. **Konfigurace sestavení** — obsluha přepíše `.env` na serveru a výloha
 *    se přeloží; to je stejně krok, kterým prochází každá její změna. Žádná
 *    migrace, žádný požadavek navíc, žádná obrazovka k údržbě.
 *
 * Zvolena trojka. Není to nový mechanismus — `StorefrontContext` už takhle
 * čte `VITE_ADMIN_URL`.
 *
 * AŽ TO BUDE CHTÍT DO ADMINISTRACE: správné místo je payload vzhledu
 * (`/api/shop/storefront`) — načítá se na každé stránce tak jako tak a je
 * editovatelný v builderu, takže by to nestálo ani požadavek navíc, ani
 * novou obrazovku. Tenhle soubor je jediné místo, kde se zdroj přepíná;
 * komponenty se měnit nebudou.
 *
 * ŽÁDNÝ CIZÍ KÓD
 * --------------
 * Odkazy jsou obyčejné `<a>`. Nenačítá se žádný widget, žádné SDK, žádný
 * skript třetí strany — dokud zákazník neklikne, neodejde na Facebook ani
 * bajt a nikdo ho nesleduje.
 */

/** Sítě, které umíme pojmenovat a nakreslit. Pořadí drží pořadí v patičce. */
export const SOCIAL_NETWORKS = ['facebook', 'instagram', 'youtube', 'x', 'tiktok'] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

export interface SocialLink {
  readonly network: SocialNetwork;
  readonly url: string;
}

/** Proměnná prostředí pro každou síť. */
const ENV_KEYS: Record<SocialNetwork, string> = {
  facebook: 'VITE_SOCIAL_FACEBOOK',
  instagram: 'VITE_SOCIAL_INSTAGRAM',
  youtube: 'VITE_SOCIAL_YOUTUBE',
  x: 'VITE_SOCIAL_X',
  tiktok: 'VITE_SOCIAL_TIKTOK',
};

/** Lidský název sítě. Značky se nepřekládají, jsou ve všech jazycích stejné. */
export const SOCIAL_LABELS: Record<SocialNetwork, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  x: 'X',
  tiktok: 'TikTok',
};

/**
 * Adresa profilu, nebo `null`.
 *
 * Pouští se JEN `https://`. Konfiguraci píše obsluha, ne návštěvník, ale
 * překlep typu `javascript:…` nebo `//zlo.example` by se propsal na každou
 * stránku obchodu — a `href` je přesně to místo, kde se z textu stává
 * spustitelná věc. Kontrola stojí jeden `try` a nestojí za to ji vynechat.
 */
export function readSocialUrl(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  try {
    const url = new URL(raw.trim());
    return url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * Nastavené odkazy. Nenastavená síť prostě chybí — patička se nesmí
 * vykreslit s prázdnou ikonou ani s odkazem na neexistující profil.
 */
export function socialLinks(): readonly SocialLink[] {
  const env = import.meta.env as unknown as Record<string, unknown>;
  const links: SocialLink[] = [];
  for (const network of SOCIAL_NETWORKS) {
    const url = readSocialUrl(env[ENV_KEYS[network]]);
    if (url !== null) links.push({ network, url });
  }
  return links;
}
